import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { isValidSubject, isValidTrack } from "./taxonomy";
import { getAccountType } from "./identity";
import { isPresetAvatarPhotoURL, isSupportedAvatarPhotoURL } from "./avatarPresets";
import type { UserProfile } from "../types/models";
import { upsertPublicProfile } from "./publicProfiles";

const db = getFirestore();

export function isProfileComplete(p: UserProfile | null | undefined) {
  const accountType = getAccountType(p);
  if (!accountType) return false;
  if (!p?.username) return false;

  let academicProfileComplete = false;
  if (accountType === "student") {
    academicProfileComplete = !!p.track && isValidTrack(p.track);
  } else if (accountType === "teacher") {
    academicProfileComplete = !!p.mainSubject && isValidSubject(p.mainSubject);
  } else {
    // Institution-specific onboarding is intentionally deferred. A server-
    // approved institution with a stable username is already a valid account.
    return accountType === "institution";
  }

  if (!academicProfileComplete) return false;

  const completionTracked = Object.prototype.hasOwnProperty.call(p, "onboardingCompletedAt");
  if (completionTracked) {
    return !!p.onboardingCompletedAt && isSupportedAvatarPhotoURL(p.photoURL);
  }

  // Historical personal profiles did not track avatar onboarding and remain
  // usable with the shared visual fallback. A stored preset, however, only
  // comes from the new flow and is not complete until its final marker exists.
  return !isPresetAvatarPhotoURL(p.photoURL);
}

export async function ensureUserDoc(uid: string, email?: string | null) {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, {
      uid,
      email: email || "",
      role: "",
      accountType: "",
      displayName: "",
      username: "",
      teacherVerified: false,
      verificationStatus: "unverified",
      track: "",
      mainSubject: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { profile: null, created: true };
  }

  const profile = snap.data() as UserProfile;

  if (isProfileComplete(profile)) {
    try {
      await upsertPublicProfile(uid, profile, { includeCreatedAt: true });
    } catch (error) {
      if (__DEV__) console.warn("public profile backfill skipped", error);
    }
  }

  return { profile, created: false };
}
